import type {StoreApi} from 'zustand/vanilla';

import type {EagGridState, EagGridStore} from '../createEagGridStore';

type KeyboardNavActions = Pick<
  EagGridStore,
  | 'enterRows'
  | 'moveRow'
  | 'enterActions'
  | 'moveAction'
  | 'exitToSearch'
  | 'clearKeyboardFocus'
  | 'clampFocusedRow'
  | 'focusActionsAt'
  | 'moveColumn'
  | 'focusCellAt'
>;

/** Keyboard-navigation actions. Bodies moved verbatim from createEagGridStore.ts. */
export function createKeyboardNavActions(store: StoreApi<EagGridState>): KeyboardNavActions {
  return {
    enterRows: () => {
      store.setState({
        focusZone: 'row',
        focusedRowIndex: 0,
        focusedActionIndex: 0,
        focusedColumnIndex: 0,
      });
    },

    moveRow: (delta: number, rowCount: number) => {
      if (rowCount <= 0) {
        return;
      }
      const current = store.getState().focusedRowIndex ?? 0;
      const next = Math.max(0, Math.min(current + delta, rowCount - 1));
      store.setState({focusZone: 'row', focusedRowIndex: next, focusedActionIndex: 0});
    },

    enterActions: () => {
      store.setState({focusZone: 'actions', focusedActionIndex: 0});
    },

    moveAction: (delta: number, actionCount: number) => {
      if (actionCount <= 0) {
        return;
      }
      const next = Math.max(
        0,
        Math.min(store.getState().focusedActionIndex + delta, actionCount - 1)
      );
      store.setState({focusedActionIndex: next});
    },

    exitToSearch: () => {
      if (store.getState().focusZone === null) {
        return;
      }
      store.setState({focusZone: null});
    },

    clearKeyboardFocus: () => {
      store.setState({
        focusZone: null,
        focusedRowIndex: null,
        focusedActionIndex: 0,
        focusedColumnIndex: null,
      });
    },

    clampFocusedRow: (rowCount: number) => {
      const {focusedRowIndex} = store.getState();
      if (focusedRowIndex === null) {
        return;
      }
      if (rowCount <= 0) {
        store.setState({focusedRowIndex: null, focusZone: null});
        return;
      }
      if (focusedRowIndex > rowCount - 1) {
        store.setState({focusedRowIndex: rowCount - 1});
      }
    },

    focusActionsAt: (rowIndex: number, actionIndex: number) => {
      const state = store.getState();
      if (
        state.focusZone === 'actions' &&
        state.focusedRowIndex === rowIndex &&
        state.focusedActionIndex === actionIndex
      ) {
        return;
      }
      store.setState({
        focusZone: 'actions',
        focusedRowIndex: rowIndex,
        focusedActionIndex: actionIndex,
      });
    },

    moveColumn: (delta: number, columnCount: number) => {
      if (columnCount <= 0) {
        return;
      }
      const current = store.getState().focusedColumnIndex ?? 0;
      const next = Math.max(0, Math.min(current + delta, columnCount - 1));
      store.setState({focusedColumnIndex: next});
    },

    focusCellAt: (rowIndex: number, columnIndex: number) => {
      const state = store.getState();
      if (
        state.focusZone === 'row' &&
        state.focusedRowIndex === rowIndex &&
        state.focusedColumnIndex === columnIndex
      ) {
        return;
      }
      store.setState({
        focusZone: 'row',
        focusedRowIndex: rowIndex,
        focusedColumnIndex: columnIndex,
      });
    },
  };
}
